import type { HTTPMethod } from '@readme/iso';

import React from 'react';

import classy from '@core/utils/classy';

import './style.scss';

interface Props {
  className?: string;
  fixedWidth?: boolean;
  /**
   *  Is the method embedded in an active sidebar item?
   */
  isActive?: boolean;
  prefix?: React.ReactNode;
  size?: 'lg' | 'md';
  /**
   *  Sets the styling of the method badge
   */
  style?: 'classic' | 'modern';
  suffix?: React.ReactNode;
  type: HTTPMethod;
}

function getName(type: Props['type'], size: Props['size']) {
  // Convert to lowercase for class name
  const name = type.toLowerCase();

  switch (name) {
    case 'options':
      return 'optns';
    case 'connect':
      return 'cnnct';
    case 'delete':
      return size === 'lg' ? 'delete' : 'del';
    case 'requestbody':
      return 'rqbdy';
    case 'responses':
      return 'rspns';
    case 'description':
      return 'descr';
    case 'webhook':
      return 'wbhk';
    default:
      return name;
  }
}

const APIMethod = React.memo(function APIMethod({
  className,
  fixedWidth,
  isActive = false,
  prefix,
  size = 'md',
  style = 'classic',
  suffix,
  type,
  ...attrs
}: Props) {
  return (
    <span
      {...attrs}
      className={classy(
        'rm-APIMethod',
        'APIMethod',
        style === 'modern' && 'APIMethod_modern',
        fixedWidth && 'APIMethod_fixedWidth',
        fixedWidth && `APIMethod_fixedWidth_${size}`,
        `APIMethod_${getName(type, 'md')}`,
        `APIMethod_${size}`,
        className,
      )}
      data-testid="http-method"
      style={{
        ['--APIMethod-bg' as string]:
          isActive || style === 'classic'
            ? `var(--APIMethod-${getName(type, 'md')}-bg-active)`
            : `var(--APIMethod-${getName(type, 'md')}-bg)`,
        ['--APIMethod-fg' as string]:
          isActive || style === 'classic'
            ? 'var(--APIMethod-default-fg-active)'
            : `var(--APIMethod-${getName(type, 'md')}-fg)`,
        ['--APIMethod-bg-active' as string]: `var(--APIMethod-${getName(type, 'md')}-bg-active)`,
      }}
    >
      {prefix}
      {getName(type, size)}
      {suffix}
    </span>
  );
});

export default APIMethod;
